import { useUserStore } from '@/stores/user';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import {
  BPLoggeFlowType,
  BPLoggerEventType,
  type AdditionalContext,
  type BusinessProcessLoggerModel,
} from '@onrunning/backstage-models';
import { v4 as uuidv4 } from 'uuid';
import { AppSettings } from '../constants/app.settings';

const options = {
  init: {
    distributed_tracing: {
      enabled: true,
      cors_use_newrelic_header: true,
      cors_use_tracecontext_headers: true,
      allowed_origins: [
        '%VITE_APP_onBackstageAPI%',
        '%VITE_APP_onProductsAPI%',
        '%VITE_APP_onBackstage%',
        '%VITE_APP_onForce%',
      ],
    },
    privacy: {
      cookies_enabled: false,
    },
  },
  info: {
    beacon: '%VITE_APP_newRelicBeacon%',
    errorBeacon: '%VITE_APP_newRelicBeacon%',
    licenseKey: '%VITE_APP_newRelicLicenseKey%',
    applicationID: '%VITE_APP_newRelicApplicationID%',
    sa: 1,
  },
  loader_config: {
    accountID: '%VITE_APP_newRelicAccountID%',
    trustKey: '%VITE_APP_newRelicTrustKey%',
    agentID: '%VITE_APP_newRelicAgentID%',
    licenseKey: '%VITE_APP_newRelicLicenseKey%',
    applicationID: '%VITE_APP_newRelicApplicationID%',
  },
};

const nrAgent: BrowserAgent = new BrowserAgent(options);

export function useBPLogger() {
  const createBusinessProcessBPLog = (
    operationSuccessful: boolean,
    flow: BPLoggeFlowType,
    eventType: BPLoggerEventType,
    additionalContext?: AdditionalContext,
    duration_ms?: number,
  ): string => {
    const traceId = uuidv4();
    const { userProfile, selectedAccount, selectedAccountId } = useUserStore();
    const userContext = {
      user_id: userProfile?.profile?.id || '',
      language_iso_code: userProfile?.user?.locale || '',
      selected_account_id: selectedAccountId,
      selected_account_legal_entity: selectedAccount?.legalEntity,
      selected_account_country_id: selectedAccount?.countryId,
      selected_account_billing_country: selectedAccount?.billingCountry,
      selected_account_account_trading_currency: selectedAccount?.accountTradingCurrency,
      selected_account_shipping_location_id: selectedAccount?.shippingLocationId,
      channel_type: 'web',
    };

    const log: BusinessProcessLoggerModel = {
      logger: 'business_process_logger',
      service_version: '1.0.0',
      trace_id: traceId || '',
      flow,
      event_type: eventType,
      is_successful: operationSuccessful,
      origin_service: 'backstage-ui',
      environment: AppSettings.rollbarEnv,
      additional_context: { ...userContext, ...additionalContext, duration_ms } as AdditionalContext,
    };

    return JSON.stringify(log);
  };

  const createBPLogFactory =
    (flow: BPLoggeFlowType, eventType: BPLoggerEventType) =>
    (operationSuccessful: boolean, additionalContext?: AdditionalContext, duration_ms?: number) =>
      nrAgent.log(createBusinessProcessBPLog(operationSuccessful, flow, eventType, additionalContext, duration_ms));

  const loginUserBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.Login);
  const logoutUserBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.Logout);
  const submitOrderBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.SubmitOrder);
  const addToCartBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.AddToCart);
  const completePaymentBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.CompletePayment);
  const submitShippingAddressBPLog = createBPLogFactory(
    BPLoggeFlowType.OrderToCash,
    BPLoggerEventType.SubmitShippingAddress,
  );
  const pageViewBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.PageView);
  const createOrderInvoiceBPLog = createBPLogFactory(BPLoggeFlowType.OrderToCash, BPLoggerEventType.CreateOrderInvoice);

  return {
    loginUserBPLog,
    logoutUserBPLog,
    submitOrderBPLog,
    addToCartBPLog,
    completePaymentBPLog,
    submitShippingAddressBPLog,
    pageViewBPLog,
    createOrderInvoiceBPLog,
  };
}
